import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { VyIcon } from '@vy/ui-design-system';
import { isEmptyArray } from '@vy/utils';

import { Price } from '../../../../../components/design/Price';
import { useSubsidiaryNameMap } from '../../../../../hooks/requests/use-subsidiary-name-map';
import { InvoicesHighlightsProps } from './InvoicesHighlights';
import { getFilterLabel, formatFilterValue } from '../../invoices-page-utils/format-utils';
import { formatDatesDisplay } from '../../invoices-page-utils/date-display-utils';

type FilterBasedViewProps = Pick<
  InvoicesHighlightsProps,
  'filters' | 'dates' | 'invoicesCount' | 'invoicesHighlightsSummary'
>;

type FilterDisplay = {
  label: string;
  value: string;
};

export const FilterBasedView: React.FC<FilterBasedViewProps> = ({
  filters,
  dates,
  invoicesCount,
  invoicesHighlightsSummary,
}) => {
  const dateDisplays = useMemo<FilterDisplay[]>(() => {
    if (!dates || isEmptyArray(dates)) {
      return [];
    }

    return [
      {
        label: 'Issue date',
        value: formatDatesDisplay(dates),
      },
    ];
  }, [dates]);

  const { subsidiaryNameById } = useSubsidiaryNameMap();

  const filterDisplays = useMemo<FilterDisplay[]>(() => {
    if (!filters || filters.length === 0) {
      return [];
    }

    return filters.map((filter) => ({
      label: getFilterLabel(filter),
      value: formatFilterValue(filter, subsidiaryNameById),
    }));
  }, [filters, subsidiaryNameById]);

  const totalAmount = invoicesHighlightsSummary?.totalAmount || 0;

  const allDisplays = useMemo(() => [...dateDisplays, ...filterDisplays], [dateDisplays, filterDisplays]);

  if (isEmptyArray(allDisplays)) {
    return null;
  }

  return (
    <Box
      sx={{
        overflowX: 'auto',
        overflowY: 'hidden',
        mb: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          p: 3,
          backgroundColor: '#FAFBFF',
          border: '1px solid #CFE6FF',
          minWidth: 'max-content',
          height: '92px',
        }}
      >
          <Stack
            direction="row"
            spacing={3}
            divider={<Divider orientation="vertical" flexItem sx={{ borderColor: '#D6DBE7' }} />}
          >
            {allDisplays.map((filterDisplay, index) => (
              <Stack
                key={`${filterDisplay.label}-${index}`}
                direction="row"
                spacing={1}
              >
                <VyIcon
                  icon="Filter"
                  style={{
                    color: '#5083FF',
                    marginTop: '-4px',
                  }}
                />
                <Stack direction="column" spacing="4px">
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'right',
                      fontSize: '14px',
                      color: '#7E8796',
                      fontWeight: 400,
                      lineHeight: '20px',
                    }}
                  >
                    {filterDisplay.label}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '24px',
                      color: '#020F20',
                    }}
                  >
                    {filterDisplay.value}
                  </Typography>
                </Stack>
              </Stack>
            ))}

            <Stack
              direction="column"
              spacing={0.5}
              sx={{
                flex: 1,
                justifyContent: "center"
              }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#7E8796',
                  fontWeight: 400,
                }}
              >
                Total selected
              </Typography>
              <Stack direction="row" spacing={2} sx={{
                alignItems: "center"
              }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#020F20',
                    lineHeight: 1.5,
                  }}
                >
                  {invoicesCount} {invoicesCount === 1 ? 'invoice' : 'invoices'}
                </Typography>
                <Divider
                  orientation="vertical"
                  sx={{ height: 15, borderColor: '#A4ADBE' }}
                />
                <Price
                  rounded
                  variant="body1"
                  fractionDigits={0}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#020F20',
                    lineHeight: 1.5,
                  }}
                >
                  {totalAmount}
                </Price>
              </Stack>
            </Stack>
          </Stack>
      </Paper>
    </Box>
  );
};
